import { GridActionsCellItem } from "@mui/x-data-grid";
import DataTable from "pages/components/DataTable";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import MissionMovementHistoryModal from "../Modal/MissionMovementHistoryModal";
import { displayNotification, empty, getFormattedDate, sendAPIRequest } from "helpers";
import { useLazyGetMissionLatestStatusQuery, useLazyGetMissionV2Query } from "modules/pvs_activities/redux/service/missions";
import { useNavigate } from "react-router-dom";
import Row from "components/common/Row";
import Column from "components/common/Column";
import { Tab, Tabs } from "@mui/material";
import { MISSION_STATUS_LIST } from "modules/pvs_activities/constants";
import { USER_ROLES } from "constants";
import { useSelector } from "react-redux";

const MissionStatus = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authData = useSelector(s => s.auth.data);
  const [trigger, { data, isFetching }] = useLazyGetMissionLatestStatusQuery();
  const [modalData, setModalData] = useState(null);
  const [triggerV2, { data: v2Data, isFetching: isFetchingV2 }] = useLazyGetMissionV2Query();
  const [value, setValue] = useState(0);  

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (value === 0) {
      trigger({ statusID: 3 });
    } else if (value === 1) {
      triggerV2({ mission_status_id: MISSION_STATUS_LIST.ongoing ?? '' });
    }
  }, [value]);

  const deleteMission = async (missionId = null) => {
    try {
      if (missionId === null) {
        console.error('missionId is required');
        return;
      }
      if (!window.confirm(t("243", "Are you sure you want to delete?"))) {
        return;
      }
      const req = await sendAPIRequest(`/api/mission/${missionId}`, "DELETE");
      const res = await req.json();
      if (res.success) {
        displayNotification(
          "success",
          `${t("244", "Successfully deleted mission data")} [B]`
        );
        trigger({ mission_status_id: MISSION_STATUS_LIST.ongoing ?? '' });
      } else {
        displayNotification(
          "error",
          `${t("245", "Error deleting mission data")} [B]`
        );
      }
    } catch (e) {
      console.log(e);
      displayNotification(
        "error",
        `${t("245", "Error deleting mission data")} [F]`
      );
    }
  };

  const navigateUploadDocument = (missionInfo = null) => {
    navigate(
      `/portal/document-management/create?country_id=${missionInfo.country_id}&mission_id=${missionInfo.id}&region_id=${missionInfo.country.region_id}&mission_type_id=${missionInfo.mission_type_id}&mission_language_id=${missionInfo.mission_language_id}&return=/portal/mission-management/`
    );
  };

  const stageOneTable = () => 
    (<DataTable
    getRowId={(row) => row.id}
    isLoading={isFetchingV2}
    rows={v2Data?.filter((mission) => mission.status_name !== "Cancelled" || (USER_ROLES.business_admin.includes(parseInt(authData?.role_id))))}
    className="text-white bg-primary rounded-0"
    columns={[
      {
        field: "mission_code",
        headerName: t("264", "Mission Code"),
        headerClassName: "text-white bg-primary rounded-0",
        minWidth: 80,
        flex: 1,
      },
      {
        field: "region_name",
        headerName: t("38", "Region"),
        headerClassName: "text-white bg-primary rounded-0",
        flex: 1,
      },
      {
        field: "country_name",
        headerName: t("22", "Country"),
        headerClassName: "text-white bg-primary rounded-0",
        flex: 1,
      },
      {
        field: "language_name",
        headerName: t("26", "Language"),
        headerClassName: "text-white bg-primary rounded-0",
        flex: 1,
      },
      {
        field: "mission_sequence",
        type: "number",
        valueGetter: (params) => {
          return params.row?.relationSequenceType?.list_element_name
            ? params.row?.relationSequenceType?.list_element_name
            : "N/A";
        },
        headerName: t("270", "Sequence"),
        headerClassName: "text-white bg-primary rounded-0",
        flex: 1,
      },
      {
        field: "mission_type_name",
        headerName: t("42", "Type"),
        headerClassName: "text-white bg-primary rounded-0",
        flex: 1,
      },
      {
        field: "mission_start_date_formatted",
        type: "date",
        valueGetter: ({ value }) => (!empty(value) ? value : null),
        valueFormatter: ({ value }) => !empty(value) ? getFormattedDate(value) : "",
        headerName: t("277", "Start Date"),
        headerClassName: "text-white bg-primary rounded-0",
        minWidth: 80,
        flex: 1,
      },
      {
        field: "mission_end_date_formatted",
        type: "date",
        valueGetter: ({ value }) => (!empty(value) ? value : null),
        valueFormatter: ({ value }) => !empty(value) ? getFormattedDate(value) : "",
        headerName: t("278", "End Date"),
        headerClassName: "text-white bg-primary rounded-0",
        flex: 1,
      },
      {
        field: "mission_start_year",
        type: "number",
        valueFormatter: ({ value }) => value,
        headerName: t("265", "Start Year"),
        headerClassName: "text-white bg-primary rounded-0",
        flex: 1,
      },
      {
        field: "official_request_year",
        type: "number",
        valueFormatter: ({ value }) => value,
        headerName: t("1077", "Request Year"),
        headerClassName: "text-white bg-primary rounded-0",
        flex: 1,
      },
      {
        field: "status_name",
        headerName: t("705", "Status"),
        headerClassName: "text-white bg-primary rounded-0",
        flex: 1,
      },
      {
        field: "specific_content_mapping_id",
        valueGetter: (params) => {
          return params.row.relationMissionSpecificMapping?.map(i => i?.relationSpecificContent?.list_element_name || "").join(", ");
        },
        headerName: t("280", "Specific Content"),
        headerClassName: "text-white bg-primary rounded-0",
        flex: 1,
      },
      {
        field: "actions",
        type: "actions",
        headerClassName: "text-white bg-primary rounded-0",
        headerName: t("74", "Actions"),
        flex: 1,
        getActions: (params) => [
          ...(params.row.can_view == true
            ? [
              <GridActionsCellItem
                label={`${t("251", "View")} ${t(
                  "46",
                  "Mission"
                )}`}
                key={0}
                showInMenu
                onClick={() => {
                  navigate(`/portal/mission-management/${params.row.id}/view/`)
                }}
              />,
            ]
            : []),

          ...(params.row.can_edit === true
            ? [
              <GridActionsCellItem
                label={`${t("80", "Edit")} ${t(
                  "46",
                  "Mission"
                )}`}
                key={1}
                showInMenu
                onClick={() => {
                  navigate(`/portal/mission-management/${params.row.id}/edit/`)
                }}
              />,
            ]
            : []),

          ...(params.row.can_delete === true
            ? [
              <GridActionsCellItem
                showInMenu
                label={`${t("29", "Delete")} ${t(
                  "46",
                  "Mission"
                )}`}
                key={2}
                onClick={() => deleteMission(params.row.id)}
              />,
            ]
            : []),
          ...(params.row.documents &&
            params.row.documents.length > 0
            ? [
              <GridActionsCellItem
                label={`${t("251", "View")} ${t("341", "Report")} ${t(
                  "1026",
                  "Details"
                )}`}
                key={3}
                showInMenu
                onClick={() => {
                  navigate(`/portal/document-management/${params.row.documents[0]?.id ?? ''}/view/`)
                }}
              />,
            ]
            : []),
          ...(params.row.documents &&
            params.row.documents.length > 0 && params.row.can_view == true
            ? [
              <GridActionsCellItem
                label={`${t("80", "Edit")} ${t("341", "Report")} ${t(
                  "1026",
                  "Details"
                )}`}
                key={4}
                showInMenu
                onClick={() => {
                  navigate(`/portal/document-management/${params.row.documents[0]?.id ?? ''}/edit/`)
                }}
              />,
            ]
            : []),
          <GridActionsCellItem
            label={t("-1", "Upload Document")}
            key={5}
            showInMenu
            onClick={() => {
              navigateUploadDocument(params.row);
            }}
          />,
        ],
        width: 90,
      },
    ]}
  />)

  const stageTwoTable = () => (<DataTable
    getRowId={(row) => row.id}
    isLoading={isFetching}
    className="text-white bg-primary rounded-0"
    rows={data && data.length > 0 ? data : []}
    columns={[
      {
        field: "country_name",
        headerName: t("-1", "Country"),
        headerClassName: "text-white bg-primary rounded-0",
        valueGetter: (params) => {
          return params.value;
        },
        minWidth: 150,
        flex: 1,
      },
      {
        field: "activity_type",
        headerName: t("-1", "Mission Type"),
        valueGetter: (params) => {
          return params.value?.name;
        },
        headerClassName: "text-white bg-primary rounded-0",
        minWidth: 150,
        flex: 0,
      },
      {
        field: "mission_sector",
        headerName: t("-1", "Mission Sector"),
        headerClassName: "text-white bg-primary rounded-0",
        minWidth: 100,
        flex: 1,
      },
      {
        field: "relationMissionManager",
        headerName: t("-1", "Mission Manager"),
        valueGetter: (params) => {
          return params.value ? params.value?.name : "Not Assigned";
        },
        headerClassName: "text-white bg-primary rounded-0",
        minWidth: 100,
        flex: 1,
      },
      ...(value === 0
        ? [
          {
            field: "status",
            headerName: t("-1", "Status"),
            valueGetter: (params) => {
              return params.row?.latestMovement?.relationProcess
                ? params.row?.latestMovement?.relationProcess?.name
                : "No Form Completed";
            },
            headerClassName: "text-white bg-primary rounded-0",
            minWidth: 100,
            flex: 1,
          },
        ]
        : []),
      {
        field: "date",
        headerName: t("-1", "Last Updated"),
        headerClassName: "text-white bg-primary rounded-0",
        minWidth: 150,
        valueGetter: (params) => {
          return getFormattedDate(params.row?.latestMovement?.updated_date);
        },

        flex: 1,
      },
      {
        field: "actions",
        headerName: t("-1", "Actions"),
        type: "actions",
        headerClassName: "text-white bg-primary rounded-0",
        getActions: (params) => {
          const actions = [];
          actions.push(
            <GridActionsCellItem
              label={t("-1", "View Detail")}
              showInMenu
              onClick={() =>
                navigate(
                  `/portal/mission-process/${params.row.id}/create`
                )
              }
              style={{ display: "block", padding: "10px" }}
            />,
          );
          actions.push(
            <GridActionsCellItem
              label={t("-1", "View")}
              key={`missionhistory-${params.row?.id}`}
              showInMenu
              onClick={() =>
                setModalData({
                  countryName: params.row.country_name,
                  missionID: params.row.id,
                })
              }
            />
          );
          
          return actions;
        },
      },

    ]}
  />)

  return (
    <>
      <div className="col-12 bg-beige mt-6">
        <div className="card-title1 py-2 text-center text-dark">{t("-1", `Missions Status`)}</div>
      </div>
      <Row className="d-flex justify-content-center">
        <Column
          className="text-center d-flex justify-content-center align-items-center"
          col={12}
        >
          <Tabs value={value} onChange={handleChange}>
            <Tab
              className="fw-bold fs-5"
              label="Workflow Missions"
              value={0}
            />
            <Tab
              className="fw-bold fs-5"
              label="Stage 1 Missions"
              value={1}
            />
          </Tabs>
        </Column>
      </Row>
      <div className="card card-woah border border-secondary shadow rounded-0 mt-0">
        <div className="card-body p-4">
          { 
            value===0?stageTwoTable():stageOneTable()
          }
        </div>
      </div>
      {modalData && (
        <MissionMovementHistoryModal
          countryName={modalData.countryName}
          missionID={modalData.missionID}
          onClose={() => setModalData(null)}
        />
      )}
    </>
  );
};

export default MissionStatus;
